/**
 * @file DomainRequestsCard.tsx
 * @description «Заявки владельца за 30 дней» во вкладке «Домены» карточки
 * магазина (phase19.28 этап А; PL11: свой домен подключает владелец
 * платформы по заявке магазина). Строка: адрес, комментарий, кто и когда;
 * «Добавить домен» открывает форму с подставленным адресом. Заявок нет —
 * блока нет.
 */

import { Plus } from "lucide-react";

import type { ShopDomainRequest } from "../../../lib/api-platform-shops";
import { formatPlatformDateTime } from "../../../lib/platform/shop-format";
import { Button } from "../../ui/button";
import { SectionCard } from "../../ui/SectionCard";

interface Props {
  requests: readonly ShopDomainRequest[];
  /** Адреса, уже привязанные к магазину, — у таких заявок кнопки нет. */
  boundHosts: ReadonlySet<string>;
  onAdd: (host: string) => void;
}

export function DomainRequestsCard({ requests, boundHosts, onAdd }: Props) {
  if (requests.length === 0) return null;
  return (
    <SectionCard title="Заявки владельца за 30 дней">
      <ul className="flex flex-col divide-y divide-[var(--border)]" aria-label="Заявки на подключение домена">
        {requests.map((request) => {
          const bound = boundHosts.has(request.host);
          return (
            <li key={`${request.host}-${request.requestedAt}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="type-body break-all font-medium" title={request.host}>
                  {request.hostUnicode || request.host}
                </span>
                {request.comment ? <span className="type-body break-words text-on-surface-variant">{request.comment}</span> : null}
                <span className="type-caption text-on-surface-variant">
                  {formatPlatformDateTime(request.requestedAt)}
                  {request.requestedBy ? ` · ${request.requestedBy.name}` : ""}
                  {bound ? " · уже привязан" : ""}
                </span>
              </span>
              {bound ? null : (
                <Button variant="soft" className="self-start sm:self-auto" onClick={() => onAdd(request.host)}>
                  <Plus className="size-4" aria-hidden />
                  Добавить домен
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
